/**
 * PathwayCanvas (PROD-591, extended in Sprint 4.3.1 / PROD-650).
 *
 * React Flow wrapper that renders the active pathway's nodes + edges.
 * Pan, zoom, minimap, and fit-to-view controls are wired via the React
 * Flow `<Controls>` + `<MiniMap>` primitives. Node + edge data flows
 * through the pure `layoutPathway` service so the geometry stays
 * deterministic and test-friendly.
 *
 * Sprint 4.3.1 (PROD-650) introduces the Awell 3-tier hierarchy:
 * Track > Step > Action (see `awell/studio/docs/getting-started/
 * design-ontology.md`). The canvas drills into one hierarchy level at
 * a time per `hierarchyLevel`:
 *   - `'tracks'`   -> render only the Track containers + their incoming
 *                    trigger edges.
 *   - `'steps'`    -> render Steps inside the focused Track + their
 *                    transition edges.
 *   - `'actions'`  -> render Actions inside the focused Step + their
 *                    transition edges.
 *
 * Activity nodes (the legacy 6: demographics / vitals / labs / decision
 * / nba / outcome) keep their column-rank layout. Hierarchy nodes use
 * a stacked vertical layout because their geometry comes from the
 * parent-child relationship (Track contains Steps; Step contains
 * Actions) rather than the activity rank.
 *
 * Selection contract:
 *   - Clicking a node calls `onSelectNode(nodeId)`.
 *   - Clicking the canvas background calls `onSelectNode(null)`.
 *   - The currently-selected node is highlighted via the React Flow
 *     `selected` flag (passed through `nodes[i].selected`).
 *
 * Phase 1 build: nodes are NOT draggable (read-only mode). Phase 2 will
 * flip `nodesDraggable` to true once the editor exits view mode per
 * master prompt v3.3 §8 Edit Mode.
 *
 * HC6 a11y: React Flow's accessibility is limited; the editor view
 * provides a `<PathwayListFallback>` toggleable via "Show as list".
 * The canvas itself sets `aria-label` so SR users at least know the
 * region's purpose.
 *
 * HC5: zero em / en dashes anywhere.
 */
import { useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  Background,
  type BackgroundVariant,
  type Connection,
  type Edge,
  type Node,
  type NodeMouseHandler,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  HIERARCHY_NODE_TYPES,
  layoutPathway,
  type HierarchyLevel,
  type HierarchyNodeType,
  type PathwayLayoutEdgeInput,
  type PathwayLayoutNodeInput,
  type PathwayNodeType,
} from '../../services/pathway-layout';
import type { EditablePathway } from '../../state/pathway.store';
import { usePaletteArmingStore } from '../../state/palette-arming.store';
import { HIERARCHY_NODE_TYPES_MAP } from './HierarchyNodes';
import { PATHWAY_EDGE_TYPES } from './PathwayCanvasEdge';
import { PathwayCanvasMinimap } from './PathwayCanvasMinimap';
import { PathwayCanvasZoomControls } from './PathwayCanvasZoomControls';
import {
  PALETTE_ACTIVITY_TYPE_MIME,
  PALETTE_NODE_TYPE_MIME,
} from './PathwayPalette';

export interface PathwayCanvasProps {
  pathway: EditablePathway;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  /**
   * Drill-down level. When omitted, the canvas defaults to `'tracks'`
   * if the pathway carries any hierarchy nodes; otherwise it falls
   * back to the legacy activity rendering (all nodes shown). This
   * preserves backwards compatibility with the existing
   * Cardiometabolic / HRT fixtures which do not yet carry Track / Step
   * / Action wrappers.
   */
  hierarchyLevel?: HierarchyLevel;
  /** Focused parent at the current drill-down level (track or step). */
  focusedParentId?: string | null;
  /**
   * Sprint 4.3.2 (PROD-651). Per-action multiplicity count map keyed by
   * Action node id. The canvas threads each entry into the node data so
   * the <ActionNode> renders a <MultiplicityBadge> when the count >= 2.
   * Pass an empty record (or omit) to hide all badges (Design Mode).
   */
  multiplicityCountsByActionId?: Record<string, number>;
  /**
   * Sprint 4.3.7: drag-from-palette → create a node. The canvas resolves
   * the React Flow drop coordinate via `screenToFlowPosition` and
   * threads it through this callback. The `parentNodeId` is currently
   * omitted (the future Track-as-React-Flow-parent wiring resolves it
   * from the React Flow `parentNode` chain); the editor view computes a
   * sensible default from the active hierarchy level.
   */
  onAddNode?: (init: {
    nodeType: PathwayNodeType;
    activityType?: PathwayNodeType;
    position: { x: number; y: number };
  }) => void;
  /**
   * Sprint 4.3.7: drag-edge-between-nodes → create a transition. Passed
   * straight to React Flow `onConnect`. When supplied, the canvas flips
   * `nodesConnectable` true so the user can pull a wire from a node's
   * source handle to another node's target handle.
   */
  onConnect?: (connection: Connection) => void;
  className?: string;
}

const HIERARCHY_TYPE_SET = new Set<PathwayNodeType>(HIERARCHY_NODE_TYPES);

function isHierarchyType(t: PathwayNodeType): t is HierarchyNodeType {
  return HIERARCHY_TYPE_SET.has(t);
}

export function PathwayCanvas({
  pathway,
  selectedNodeId,
  onSelectNode,
  hierarchyLevel,
  focusedParentId,
  multiplicityCountsByActionId,
  onAddNode,
  onConnect,
  className,
}: PathwayCanvasProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Detect whether the pathway carries the 3-tier hierarchy. Legacy
  // pathways (Cardiometabolic etc.) keep the original layout.
  const hasHierarchy = useMemo(
    () => pathway.nodes.some((n) => isHierarchyType(n.nodeType)),
    [pathway],
  );
  const effectiveLevel: HierarchyLevel | null = hasHierarchy
    ? hierarchyLevel ?? 'tracks'
    : null;

  // Filter the visible node set per the drill-down level. Activity
  // nodes always render when `effectiveLevel` is null (legacy mode).
  //
  // Sprint 4.3.7-fix-2: when a pathway is "mixed" (contains both
  // hierarchy nodes AND legacy activity nodes - this happens when a
  // user click-to-adds a Track / Step / Action onto a canonical fixture
  // that is still legacy-shaped, OR a canonical fixture has been
  // partially migrated to the 3-tier shape) we MUST keep the orphan
  // legacy nodes visible. The old filter at the 'tracks' level returned
  // ONLY `nodeType === 'track'` which destroyed the 8 canonical legacy
  // nodes the moment the user added a Step. The fix: at every level,
  // also include legacy activity nodes whose `parentNodeId` matches the
  // current scope (orphans at root, parent-matched at deeper levels).
  // Founder-driven regression caught 2026-05-08 02:30.
  const visibleNodes = useMemo(() => {
    if (effectiveLevel === null) return pathway.nodes;
    const isHierarchy = (t: PathwayNodeType) => HIERARCHY_TYPE_SET.has(t);
    if (effectiveLevel === 'tracks') {
      return pathway.nodes.filter((n) => {
        if (n.nodeType === 'track') return true;
        // Orphan legacy + orphan hierarchy nodes (no parentNodeId)
        // surface at the root so canonical fixtures stay visible when
        // the user mixes in hierarchy nodes, and so an orphan
        // step/action a user just dropped on a parentless canvas is
        // visible until they wire it under a Track.
        if (!n.parentNodeId) return true;
        return false;
      });
    }
    if (effectiveLevel === 'steps') {
      return pathway.nodes.filter((n) => {
        if (n.nodeType === 'step' && n.parentNodeId === focusedParentId) {
          return true;
        }
        // Legacy activity nodes whose parent is the focused Track
        // surface alongside Step cards inside that Track.
        if (
          !isHierarchy(n.nodeType) &&
          n.parentNodeId === focusedParentId &&
          focusedParentId !== null
        ) {
          return true;
        }
        return false;
      });
    }
    // 'actions'
    return pathway.nodes.filter((n) => {
      if (n.nodeType === 'action' && n.parentNodeId === focusedParentId) {
        return true;
      }
      // Legacy activity nodes whose parent is the focused Step surface
      // alongside Action cards inside that Step.
      if (
        !isHierarchy(n.nodeType) &&
        n.parentNodeId === focusedParentId &&
        focusedParentId !== null
      ) {
        return true;
      }
      return false;
    });
  }, [pathway, effectiveLevel, focusedParentId]);

  const visibleNodeIds = useMemo(
    () => new Set(visibleNodes.map((n) => n.id)),
    [visibleNodes],
  );

  const visibleEdges = useMemo(
    () =>
      pathway.edges.filter(
        (e) => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target),
      ),
    [pathway.edges, visibleNodeIds],
  );

  // Sprint 4.3.5: pre-compute the action count per Step for the
  // <StepStatsRow>. Tracks pre-compute as well so a future Track-card
  // visualisation could reuse it.
  const actionCountByStepId = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const node of pathway.nodes) {
      if (node.nodeType === 'action' && node.parentNodeId) {
        counts[node.parentNodeId] = (counts[node.parentNodeId] ?? 0) + 1;
      }
    }
    return counts;
  }, [pathway.nodes]);

  const { nodes, edges } = useMemo(() => {
    const layoutInput: {
      nodes: PathwayLayoutNodeInput[];
      edges: PathwayLayoutEdgeInput[];
    } = {
      nodes: visibleNodes.map((n) => {
        const multiplicityCount =
          n.nodeType === 'action' && multiplicityCountsByActionId
            ? multiplicityCountsByActionId[n.id] ?? 0
            : 0;
        const actionCount =
          n.nodeType === 'step' ? actionCountByStepId[n.id] ?? 0 : undefined;
        return {
          id: n.id,
          nodeType: n.nodeType,
          label: n.label,
          data: {
            description: n.description ?? '',
            category: n.category,
            isDecision: n.isDecision,
            triggerType: n.triggerType,
            triggerFilter: n.triggerFilter,
            parentNodeId: n.parentNodeId,
            multiplicityCount,
            stepCategory: n.stepCategory,
            stepCategoryLabel: n.stepCategoryLabel,
            programDuration: n.programDuration,
            actionCount,
            stakeholderCount: n.stakeholderCount,
            locationCount: n.locationCount,
          },
        };
      }),
      edges: visibleEdges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.label,
      })),
    };
    return layoutPathway(layoutInput);
  }, [
    visibleNodes,
    visibleEdges,
    multiplicityCountsByActionId,
    actionCountByStepId,
  ]);

  // Decorate each node with the React Flow `type` discriminator so the
  // custom hierarchy renderers fire. Activity nodes keep `type:
  // 'default'`.
  const decoratedNodes: Node[] = useMemo(
    () =>
      nodes.map((n) => {
        const sourceNode = visibleNodes.find((sn) => sn.id === n.id);
        const isHierarchy =
          sourceNode !== undefined && isHierarchyType(sourceNode.nodeType);
        return {
          ...n,
          type: isHierarchy ? sourceNode.nodeType : 'default',
          selected: n.id === selectedNodeId,
          data: {
            ...n.data,
            label: sourceNode?.label ?? (n.data as { label: string }).label,
          },
        };
      }),
    [nodes, selectedNodeId, visibleNodes],
  );

  const decoratedEdges: Edge[] = edges;

  // Sprint 4.3.7-fix: click-to-add. Read the armed-node-type from the
  // palette-arming store. Click on canvas while armed -> create a node
  // at the click coordinate + disarm. Click on canvas while NOT armed ->
  // clear the inspector selection (legacy behaviour).
  const armedNodeType = usePaletteArmingStore((s) => s.armedNodeType);
  const armedActivityType = usePaletteArmingStore((s) => s.armedActivityType);
  const disarm = usePaletteArmingStore((s) => s.disarm);

  const handleNodeClick: NodeMouseHandler = (_evt, node) => {
    // Sprint 4.3.7-fix: when armed, the wrapper-level click handler
    // (handleWrapperClickCapture) owns add-node placement so we don't
    // double-fire. NodeClick simply suppresses selection while armed
    // - the wrapper's `if (target.closest('.react-flow__node')) return`
    // also skips, so the click while armed on a node is a no-op.
    if (armedNodeType !== null) return;
    onSelectNode(node.id);
  };

  const handlePaneClick = useCallback(() => {
    // Sprint 4.3.7-fix: pane-click fires for the React Flow background.
    // When armed, the wrapper handler runs and creates a node; we
    // intentionally do NOT also create one here (would double-fire).
    // When NOT armed, fall back to legacy "clear selection" behavior.
    if (armedNodeType !== null) return;
    onSelectNode(null);
  }, [armedNodeType, onSelectNode]);

  /**
   * Sprint 4.3.7-fix: wrapper-level click capture for armed clicks.
   * React Flow's `onPaneClick` only fires when the user clicks the
   * actual `.react-flow__pane` background; clicks on the minimap, on
   * the zoom controls, or in the wrapper area outside the React Flow
   * pane don't trigger it. Real-mouse Playwright drives showed this
   * gap. The wrapper handler is a "safety net" - when armed AND the
   * built-in `handlePaneClick` did not fire, we still place the node.
   * Marker: we set a transient flag on the event via `defaultPrevented`
   * + capture phase so clicks on real React Flow nodes (which call
   * `handleNodeClick` first) don't double-fire.
   */
  const handleWrapperClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (armedNodeType === null || !onAddNode) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      // Skip clicks on palette-armed buttons (the palette is a sibling
      // - this only matters if the wrapper somehow wraps it). Skip
      // clicks on React Flow's controls / minimap so the buttons keep
      // working while armed.
      if (
        target.closest('.react-flow__minimap') ||
        target.closest('.react-flow__controls') ||
        target.closest('[data-testid="pathway-palette"]') ||
        target.closest('[data-armed="true"][data-node-type]')
      ) {
        return;
      }
      // Sprint 4.3.7-fix: clicking on top of an existing node while
      // armed creates a new node at the click coord too. The
      // `handleNodeClick` no-ops while armed so this is the only
      // source of creation - no double-fire.
      const rect = wrapperRef.current?.getBoundingClientRect();
      const position = {
        x: rect ? event.clientX - rect.left : event.clientX,
        y: rect ? event.clientY - rect.top : event.clientY,
      };
      onAddNode({
        nodeType: armedNodeType,
        activityType: armedActivityType ?? undefined,
        position,
      });
      disarm();
    },
    [armedNodeType, armedActivityType, onAddNode, disarm],
  );

  // Sprint 4.3.7: drag-from-palette drop handler. Reads the dragged
  // node-type from `dataTransfer` and projects the pointer coordinate
  // into wrapper-relative flow coordinates. The wrapper-relative
  // coordinate is sufficient for the prototype (and is deterministic
  // for tests); a future ticket can swap in
  // `useReactFlow().screenToFlowPosition` once the editor is wrapped
  // in a `<ReactFlowProvider>`.
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!onAddNode) return;
      const data = event.dataTransfer;
      if (!data) return;
      const nodeTypeRaw =
        data.getData(PALETTE_NODE_TYPE_MIME) || data.getData('text/plain');
      if (!nodeTypeRaw) return;
      const activityTypeRaw = data.getData(PALETTE_ACTIVITY_TYPE_MIME);
      const rect = wrapperRef.current?.getBoundingClientRect();
      const position = {
        x: rect ? event.clientX - rect.left : event.clientX,
        y: rect ? event.clientY - rect.top : event.clientY,
      };
      onAddNode({
        nodeType: nodeTypeRaw as PathwayNodeType,
        activityType: activityTypeRaw
          ? (activityTypeRaw as PathwayNodeType)
          : undefined,
        position,
      });
    },
    [onAddNode],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [],
  );

  const isArmed = armedNodeType !== null;

  return (
    <div
      ref={wrapperRef}
      data-testid="pathway-canvas"
      data-pathway-id={pathway.id}
      data-selected-node-id={selectedNodeId ?? ''}
      data-hierarchy-level={effectiveLevel ?? 'legacy'}
      data-focused-parent-id={focusedParentId ?? ''}
      data-droppable={onAddNode ? 'true' : 'false'}
      data-connectable={onConnect ? 'true' : 'false'}
      data-armed={isArmed ? 'true' : 'false'}
      data-armed-node-type={armedNodeType ?? ''}
      role="region"
      aria-label="Pathway canvas"
      onDrop={onAddNode ? handleDrop : undefined}
      onDragOver={onAddNode ? handleDragOver : undefined}
      onClick={onAddNode ? handleWrapperClickCapture : undefined}
      className={[
        'relative h-[calc(100vh-12rem)] flex-1 bg-surface-base',
        isArmed ? 'cursor-crosshair' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ReactFlow
        nodes={decoratedNodes}
        edges={decoratedEdges}
        nodeTypes={HIERARCHY_NODE_TYPES_MAP}
        edgeTypes={PATHWAY_EDGE_TYPES}
        nodesDraggable={false}
        nodesConnectable={Boolean(onConnect)}
        fitView
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        onConnect={onConnect}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={'dots' as BackgroundVariant}
          gap={20}
          size={1}
          color="#cbd5e1"
        />
        <PathwayCanvasMinimap />
        <PathwayCanvasZoomControls />
      </ReactFlow>
    </div>
  );
}
